import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import qrImage from '../assets/payment_qr.jpeg';

const fmt = (n) => `₹${Number(n || 0).toFixed(2)}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);

const getWeekStart = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDay();
  const diff = (day + 6) % 7;
  const weekStart = new Date(date);
  weekStart.setDate(date.getDate() - diff);
  return weekStart.toISOString().slice(0, 10);
};

const formatWeekRange = (weekStart) => {
  const start = new Date(weekStart);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return `${weekStart} — ${end.toISOString().slice(0, 10)}`;
};

const getPrintDateClass = (value) => {
  if (!value) return null;
  const safeValue = String(value).replace(/\s+/g, '-').replace(/[^A-Za-z0-9_-]/g, '');
  return safeValue ? `print-date-${safeValue}` : null;
};

const formatPrintDateLabel = (value) => {
  if (!value) return 'N/A';
  if (typeof value !== 'string') return String(value);
  if (/^\d{4}-\d{2}$/.test(value)) {
    return `Month of ${new Date(`${value}-01`).toLocaleString('en-IN', { month: 'long', year: 'numeric' })}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(value).toLocaleDateString('en-IN');
  }
  return value;
};

const groupEmployeeOrdersDetailed = (orders, period) => {
  const grouped = orders.reduce((acc, order) => {
    const key = period === 'daily' ? order.order_date : getWeekStart(order.order_date);
    if (!acc[key]) {
      acc[key] = { key, label: period === 'daily' ? order.order_date : formatWeekRange(key), orders: [], order_count: 0, total_amount: 0 };
    }
    acc[key].orders.push(order);
    acc[key].order_count += 1;
    acc[key].total_amount += order.total_amount;
    return acc;
  }, {});
  return Object.values(grouped).sort((a, b) => a.key.localeCompare(b.key));
};

// Employee search bar
const EmployeeSearchBar = ({ onSelectEmployee, loading }) => {
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchBusy, setSearchBusy] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = async (value) => {
    setSearchInput(value);
    if (value.length >= 2) {
      setSearchBusy(true);
      try {
        const response = await api.searchEmployees(value);
        setSuggestions(response.results || []);
        setShowSuggestions(true);
      } catch (e) { setSuggestions([]); }
      finally { setSearchBusy(false); }
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setSearchBusy(false);
    }
  };

  const handleSelectEmployee = (emp) => {
    setSearchInput('');
    setSuggestions([]);
    setShowSuggestions(false);
    onSelectEmployee(emp);
  };

  return (
    <div ref={searchRef} style={{ position: 'relative', marginBottom: 20 }}>
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          placeholder="Search employee by name or code..."
          value={searchInput}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => searchInput.length >= 2 && setShowSuggestions(true)}
          style={{ width: '100%', padding: '12px 16px', fontSize: 14, borderRadius: 8, border: '1px solid var(--iocl-border)', background: 'var(--iocl-panel)', color: 'var(--iocl-white)' }}
        />
        {showSuggestions && suggestions.length > 0 && (
          <div className="search-suggestions" style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: 'var(--iocl-panel)', border: '1px solid var(--iocl-border)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.3)', zIndex: 1000 }}>
            {suggestions.map((emp) => (
              <div key={emp.id} onClick={() => handleSelectEmployee(emp)}
                style={{ padding: '10px 16px', borderBottom: '1px solid var(--iocl-border)', cursor: 'pointer' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ fontWeight: 500, color: 'var(--iocl-orange)' }}>{emp.name}</div>
                <div style={{ fontSize: 12, color: 'var(--iocl-muted)' }}>{emp.emp_code} • {emp.department_name}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      {(loading || searchBusy) && <div style={{ color: 'var(--iocl-muted)', fontSize: 12, marginTop: 6 }}>{loading ? 'Loading...' : 'Searching...'}</div>}
    </div>
  );
};

const SummaryCard = ({ title, value, icon, color = 'orange' }) => (
  <div style={{ background: 'var(--iocl-panel)', border: '1px solid var(--iocl-border)', borderRadius: 8, padding: 16, textAlign: 'center', minWidth: 120 }}>
    <div style={{ fontSize: 24, marginBottom: 4 }}>{icon}</div>
    <div style={{ fontSize: 12, color: 'var(--iocl-muted)', marginBottom: 4 }}>{title}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: `var(--iocl-${color})` }}>{value}</div>
  </div>
);

const PrintInvoice = ({ title, employee, date, orders, aggregateItems = false }) => {
  if (!orders || orders.length === 0) return null;
  const getUnitPrice = (item) => item.unit_price ?? item.price ?? item.rate ?? 0;
  const getSubtotal = (item) => item.subtotal ?? item.quantity * getUnitPrice(item);
  // created_at is deliberately null on the aggregate row: it stands for a whole
  // billing period, not a moment. Feeding the period string to Date() used to
  // print a spurious "05:30 AM" (UTC midnight rendered in IST) on every bill.
  const invoiceOrders = aggregateItems
    ? [{ id: 'Summary', created_at: null, total_amount: orders.reduce((s, o) => s + Number(o.total_amount || 0), 0),
        items: Object.values(orders.flatMap(o => o.items || []).reduce((acc, item) => {
          const key = `${item.item_name}::${getUnitPrice(item)}`;
          if (!acc[key]) acc[key] = { ...item, quantity: 0, subtotal: 0 };
          acc[key].quantity += item.quantity || 0;
          acc[key].subtotal += getSubtotal(item);
          return acc;
        }, {})) }]
    : orders;
  const total = invoiceOrders.reduce((s, o) => s + Number(o.total_amount || 0), 0);
  const displayDate = formatPrintDateLabel(date);
  const isSpecificDay = /^\d{4}-\d{2}-\d{2}$/.test(date);
  return (
    <div className="print-bill-section">
      <div className="print-bill-card">
        <div className="print-bill-header">
          <div>
            <div className="print-bill-company">Indian Oil Corporation</div>
            <div className="print-bill-title">{title}</div>
            <div className="print-bill-info-row">
              <span>Employee: <strong>{employee.name}</strong> ({employee.emp_code || 'N/A'})</span>
              <span>Department: {employee.department || 'N/A'}</span>
              <span>{isSpecificDay ? 'Bill Date' : 'Billing period'}: {displayDate}</span>
            </div>
          </div>
          <div className="print-bill-qr-wrap">
            <img src={qrImage} alt="Payment QR" className="print-bill-qr" />
            <div className="print-bill-qr-label">Scan to pay</div>
          </div>
        </div>
        <div className="print-bill-summary">
          <div><div className="print-bill-summary-label">Billing period</div><div className="print-bill-summary-value">{displayDate}</div></div>
          {/* Count the real orders, not the rendered rows: aggregation collapses
              the period into one synthetic row, which reported a 12-visit month
              as "1 order". */}
          <div><div className="print-bill-summary-label">Orders</div><div className="print-bill-summary-value">{orders.length}</div></div>
          <div><div className="print-bill-summary-label">Total Amount</div><div className="print-bill-summary-value total">{fmt(total)}</div></div>
        </div>
        <div className="print-bill-orders">
          {invoiceOrders.map((order) => (
            <div key={order.id} className="print-bill-order">
              <div className="print-bill-order-title">
                <span>{order.id === 'Summary' ? `All orders (${orders.length})` : `Order #${order.id}`}</span>
                <span>{order.created_at ? new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                <span>{fmt(order.total_amount)}</span>
              </div>
              <table className="print-bill-table">
                <thead><tr><th>Item</th><th>Qty</th><th>Rate</th><th>Subtotal</th></tr></thead>
                <tbody>
                  {(order.items || []).map((item, idx) => (
                    <tr key={idx}><td>{item.item_name}</td><td>{item.quantity}</td><td>{fmt(getUnitPrice(item))}</td><td>{fmt(getSubtotal(item))}</td></tr>
                  ))}
                </tbody>
              </table>
              <div className="print-bill-order-total"><span>Order Total</span><span>{fmt(order.total_amount)}</span></div>
              {order.remarks && <div className="print-bill-remarks">Remarks: {order.remarks}</div>}
            </div>
          ))}
        </div>
        <div className="print-bill-total-row">
          <div className="print-bill-total-label">Grand Total</div>
          <div className="print-bill-total-value">{fmt(total)}</div>
        </div>
      </div>
    </div>
  );
};

const PERIODS = ['monthly', 'weekly', 'daily'];
const titleCase = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const periodBillTitle = (period) => `Employee ${titleCase(period)} Bill`;

// Renders the bills for whichever period is selected. Monthly collapses the
// whole span into one aggregated bill; weekly and daily emit one bill per
// group, so "Print Weekly" actually yields weekly bills. Previously every
// selection printed the same month-wide aggregate and only the heading changed.
const PeriodInvoices = ({ period, employee, periodLabel, orders }) => {
  if (!orders || orders.length === 0) return null;
  if (period === 'monthly') {
    return <PrintInvoice title={periodBillTitle(period)} employee={employee} date={periodLabel} orders={orders} aggregateItems />;
  }
  return groupEmployeeOrdersDetailed(orders, period).map((group) => (
    <PrintInvoice key={group.key} title={periodBillTitle(period)} employee={employee} date={group.label} orders={group.orders} />
  ));
};

// Monthly / Weekly / Daily selector, shared by both report modals so they
// behave identically.
const PeriodTabs = ({ value, onChange, disabled }) => (
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
    {PERIODS.map((period) => (
      <button key={period} type="button" disabled={disabled}
        className={`btn ${value === period ? 'btn-primary' : 'btn-secondary'} btn-sm`}
        onClick={() => onChange(period)}>
        {titleCase(period)}
      </button>
    ))}
  </div>
);

// Detailed report modal (for Employee Search tab)
const DetailedReportModal = ({ employee, profile, history, onClose, onPrint, printMode, printDate }) => {
  const [localHistory, setLocalHistory] = useState(history);
  const [expandedDates, setExpandedDates] = useState({});
  const [historyMonth, setHistoryMonth] = useState(thisMonth());
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthError, setMonthError] = useState(null);
  const [reportPeriod, setReportPeriod] = useState('monthly');

  // A new employee.id means a fresh selection — reset to the current month
  // rather than carrying over whatever month the previous employee's report
  // was left on.
  useEffect(() => {
    setLocalHistory(history);
    setExpandedDates({});
    setHistoryMonth(thisMonth());
    setMonthError(null);
  }, [employee.id, history]);

  const toggleDateExpand = (date) => setExpandedDates(prev => ({ ...prev, [date]: !prev[date] }));

  const handleMonthChange = async (value) => {
    setHistoryMonth(value);
    setMonthLoading(true);
    setMonthError(null);
    try {
      const data = await api.getEmployeeHistory(employee.id, { month: value });
      setLocalHistory(data);
    } catch (e) {
      setMonthError(e.message || 'Could not load orders for this month.');
    }
    setMonthLoading(false);
  };

  const ordersByDate = localHistory.grouped_by_date || {};
  const sortedDates = Object.keys(ordersByDate).sort().reverse();
  const allOrders = sortedDates.flatMap((date) => ordersByDate[date]);
  const periodLabel = localHistory.summary?.date_range?.month || historyMonth;

  // Weekly regroups the same orders; monthly and daily both list day by day
  // (monthly differs only in printing one aggregated bill instead of one per day).
  const listGroups = reportPeriod === 'weekly'
    ? groupEmployeeOrdersDetailed(allOrders, 'weekly').reverse()
    : sortedDates.map((date) => ({ key: date, label: date, orders: ordersByDate[date] }));
  const groupPrintLabel = reportPeriod === 'weekly' ? 'Print Week' : 'Print Day';
  const printGroup = printMode === 'employee-day-report' && printDate
    ? listGroups.find((g) => g.key === printDate) : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg print-section" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90%' }}>
        <div className="modal-header">
          <div>
            <div style={{ fontFamily: 'Rajdhani', fontSize: 18, fontWeight: 700 }}>Employee Report — {profile.name}</div>
            <div style={{ color: 'var(--iocl-muted)', marginTop: 4, fontSize: 13 }}>{profile.emp_code} • {profile.department} • {profile.status}</div>
            <PeriodTabs value={reportPeriod} onChange={setReportPeriod} disabled={allOrders.length === 0} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
            <div>
              <input type="month" className="month-picker" value={historyMonth} max={thisMonth()}
                disabled={monthLoading} onChange={(e) => handleMonthChange(e.target.value)} />
              {monthError && <div style={{ color: 'var(--iocl-red)', fontSize: 11, marginTop: 4, textAlign: 'right' }}>{monthError}</div>}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-success btn-sm" onClick={() => onPrint('employee-detailed-report')} disabled={allOrders.length === 0}>
                Print {titleCase(reportPeriod)}
              </button>
              <button className="btn btn-secondary btn-sm" onClick={onClose}>✕ Close</button>
            </div>
          </div>
        </div>
        <div className="modal-content-scroll" style={{ padding: 16 }}>
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ color: 'var(--iocl-orange)', marginBottom: 10, fontSize: 13, fontWeight: 700 }}>Employee Information</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
              {[['Code', profile.emp_code || 'N/A'], ['Name', profile.name], ['Department', profile.department], ['Role', profile.role], ['Joined', profile.joining_date?.slice(0, 10)]].map(([l, v]) => (
                <div key={l} style={{ background: 'var(--iocl-panel)', padding: 10, borderRadius: 6 }}>
                  <div style={{ fontSize: 11, color: 'var(--iocl-muted)' }}>{l}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--iocl-white)' }}>{v}</div>
                </div>
              ))}
              <div style={{ background: 'var(--iocl-panel)', padding: 10, borderRadius: 6 }}>
                <div style={{ fontSize: 11, color: 'var(--iocl-muted)' }}>Status</div>
                <span className={`badge badge-${profile.status === 'Active' ? 'green' : 'red'}`}>{profile.status}</span>
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ color: 'var(--iocl-orange)', marginBottom: 10, fontSize: 13, fontWeight: 700 }}>Summary Statistics</h3>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <SummaryCard title="Total Orders" value={profile.summary.total_orders} icon="📦" />
              <SummaryCard title="Total Spent" value={fmt(profile.summary.total_orders_amount)} icon="💰" />
              <SummaryCard title="Avg Order" value={fmt(profile.summary.average_order_value)} icon="📈" />
              <SummaryCard title="Last Order" value={profile.last_order_date?.slice(5) || 'N/A'} icon="📅" />
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <h3 style={{ color: 'var(--iocl-orange)', marginBottom: 10, fontSize: 13, fontWeight: 700 }}>Order History ({localHistory.summary?.total_orders || 0} orders)</h3>
            {listGroups.length === 0 ? (
              <div className="empty-state"><p>No orders found.</p></div>
            ) : listGroups.map((group) => {
              const orders = group.orders;
              const dayTotal = orders.reduce((s, o) => s + o.total_amount, 0);
              const isExpanded = expandedDates[group.key];
              return (
                <div key={group.key} style={{ background: 'var(--iocl-panel)', border: '1px solid var(--iocl-border)', borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
                  <div onClick={() => toggleDateExpand(group.key)} style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none', background: 'var(--iocl-slate)' }}>
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--iocl-white)' }}>{group.label}</div>
                      <div style={{ fontSize: 12, color: 'var(--iocl-muted)' }}>{orders.length} order{orders.length !== 1 ? 's' : ''} • {fmt(dayTotal)}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); onPrint('employee-day-report', group.key); }} type="button">{groupPrintLabel}</button>
                      <span style={{ color: 'var(--iocl-orange)' }}>{isExpanded ? '▼' : '▶'}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: 12, borderTop: '1px solid var(--iocl-border)' }}>
                      {orders.map((order) => (
                        <div key={order.id} style={{ marginBottom: 10, padding: 10, background: 'var(--iocl-panel)', borderRadius: 6, border: '1px solid var(--iocl-border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                            <div style={{ fontWeight: 600, color: 'var(--iocl-white)' }}>
                              Order #{order.id}{reportPeriod === 'weekly' ? ` — ${order.order_date}` : ''}
                            </div>
                            <div style={{ fontWeight: 700, color: 'var(--iocl-orange)' }}>{fmt(order.total_amount)}</div>
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--iocl-light)' }}>
                            {(order.items || []).map((item, idx) => <div key={idx}>{item.item_name} x{item.quantity} = {fmt(item.subtotal)}</div>)}
                          </div>
                          {order.remarks && <div style={{ fontSize: 11, color: 'var(--iocl-muted)', marginTop: 6 }}>Remarks: {order.remarks}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {localHistory.summary && (
            <div style={{ background: 'var(--iocl-panel)', padding: 14, borderRadius: 8, border: '1px solid var(--iocl-border)', textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--iocl-muted)', marginBottom: 6 }}>
                Period: {localHistory.summary.date_range?.month || (localHistory.summary.date_range?.from && localHistory.summary.date_range?.to ? `${localHistory.summary.date_range.from} to ${localHistory.summary.date_range.to}` : 'All Time')}
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: 'var(--iocl-orange)' }}>Total: {fmt(localHistory.summary.total_amount)} ({localHistory.summary.total_orders} orders)</div>
            </div>
          )}
        </div>
        {printGroup && (
          <PrintInvoice title={periodBillTitle(reportPeriod === 'weekly' ? 'weekly' : 'daily')}
            employee={profile} date={printGroup.label} orders={printGroup.orders} />
        )}
        {printMode === 'employee-detailed-report' && (
          <PeriodInvoices period={reportPeriod} employee={profile} periodLabel={periodLabel} orders={allOrders} />
        )}
      </div>
    </div>
  );
};

// Main Reports component
export default function Reports({ user }) {
  // Namespace the localStorage key by user id so two accounts on the same
  // browser (e.g. an admin who also tried the demo flow, or a shared kiosk)
  // don't see each other's paid/unpaid ticks. On first mount before user is
  // known we fall back to a session-scoped key.
  const paymentStatusKey = user?.id ? `reportPaymentStatus:u${user.id}` : 'reportPaymentStatus:anon';
  const [tab, setTab] = useState('monthly');
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeOrders, setEmployeeOrders] = useState([]);
  const [employeeReportPeriod, setEmployeeReportPeriod] = useState('monthly');
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [printMode, setPrintMode] = useState(null);
  const [printDate, setPrintDate] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(() => {
    try { return JSON.parse(localStorage.getItem(paymentStatusKey) || '{}'); } catch { return {}; }
  });
  const [searchEmployee, setSearchEmployee] = useState(null);
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [employeeHistory, setEmployeeHistory] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const printTimerRef = useRef(null);

  useEffect(() => { localStorage.setItem(paymentStatusKey, JSON.stringify(paymentStatus)); }, [paymentStatus, paymentStatusKey]);

  useEffect(() => {
    if (!printMode) return;
    const className = `print-${printMode}`;
    const dateClass = getPrintDateClass(printDate);
    document.body.classList.add(className);
    if (dateClass) document.body.classList.add(dateClass);
    const handleAfterPrint = () => {
      document.body.classList.remove(className);
      if (dateClass) document.body.classList.remove(dateClass);
      setPrintMode(null); setPrintDate(null);
      window.removeEventListener('afterprint', handleAfterPrint);
      if (printTimerRef.current) { clearTimeout(printTimerRef.current); printTimerRef.current = null; }
    };
    window.addEventListener('afterprint', handleAfterPrint);
    printTimerRef.current = window.setTimeout(() => requestAnimationFrame(() => window.print()), 500);
    return () => {
      if (printTimerRef.current) { clearTimeout(printTimerRef.current); printTimerRef.current = null; }
      window.removeEventListener('afterprint', handleAfterPrint);
      document.body.classList.remove(className);
      if (dateClass) document.body.classList.remove(dateClass);
    };
  }, [printMode, printDate]);

  const load = async () => {
    setLoading(true);
    try {
      const [monthly, items, daily] = await Promise.all([
        api.getMonthlySummary(month), api.getItemConsumption(month), api.getDailyTotals(month),
      ]);
      setData({ monthly, items, daily });
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { setSelectedEmployee(null); setEmployeeOrders([]); load(); }, [month]);

  const fetchEmployeeOrders = async (employee) => {
    setOrdersLoading(true);
    setSelectedEmployee(employee);
    setEmployeeReportPeriod('monthly');
    try {
      const response = await api.getEmployeeOrders(employee.employee_id, month);
      setEmployeeOrders(response.orders || []);
    } catch (e) { setEmployeeOrders([]); }
    setOrdersLoading(false);
  };

  const handlePrint = (mode, date = null) => { setPrintDate(date); setPrintMode(mode); };

  const handleSelectEmployee = async (employee) => {
    setSearchEmployee(employee);
    setReportError(null);
    setReportLoading(true);
    try {
      const [profile, history] = await Promise.all([
        api.getEmployeeProfile(employee.id), api.getEmployeeHistory(employee.id, { month: thisMonth() }),
      ]);
      setEmployeeProfile(profile); setEmployeeHistory(history);
    } catch (e) {
      // Previously swallowed: a failing request left the panel blank with no
      // hint that anything had gone wrong.
      setSearchEmployee(null); setEmployeeProfile(null); setEmployeeHistory(null);
      setReportError(e.message || 'Could not load this employee’s report.');
    }
    setReportLoading(false);
  };

  const totalMonthly = data.monthly?.data?.reduce((s, r) => s + r.total_spent, 0) || 0;
  const employeePrintGroups = groupEmployeeOrdersDetailed(employeeOrders, employeeReportPeriod);
  const selectedPrintGroup = printMode === 'employee-day-report' && printDate
    ? employeePrintGroups.find((g) => g.key === printDate) : null;
  const selectedEmployeeForBill = selectedEmployee
    ? { name: selectedEmployee.employee_name, emp_code: selectedEmployee.emp_code, department: selectedEmployee.department }
    : null;

  return (
    <div className="page-body">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <div className="cat-tabs" style={{ flex: 1, marginBottom: 0 }}>
          {[['search', '🔍 Employee Search'], ['monthly', '👥 Employee Summary'], ['items', '🍱 Item Consumption'], ['daily', '📅 Day-wise Totals']].map(([key, label]) => (
            <button key={key} className={`cat-tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>
        <div>
          <label style={{ fontSize: 11, display: 'block', marginBottom: 4 }}>Month</label>
          <input type="month" className="month-picker" value={month} onChange={e => setMonth(e.target.value)} />
        </div>
      </div>

      {tab === 'search' ? (
        <div className="report-panel card" style={{ padding: 20 }}>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontFamily: 'Rajdhani', fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Employee Search</div>
            <div style={{ color: 'var(--iocl-muted)', fontSize: 13 }}>Search for an employee to view their complete order history and print bills.</div>
          </div>
          <EmployeeSearchBar onSelectEmployee={handleSelectEmployee} loading={reportLoading} />
          {reportError && (
            <div style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--iocl-red)', color: 'var(--iocl-red)', fontSize: 13 }}>
              {reportError}
            </div>
          )}
          {searchEmployee && employeeProfile && employeeHistory && (
            <DetailedReportModal employee={searchEmployee} profile={employeeProfile} history={employeeHistory}
              onClose={() => { setSearchEmployee(null); setEmployeeProfile(null); setEmployeeHistory(null); }}
              onPrint={handlePrint} printMode={printMode} printDate={printDate}
            />
          )}
        </div>
      ) : loading ? (
        <div className="loading-center"><span className="spinner" /> Loading report...</div>
      ) : (
        <>
          {tab === 'monthly' && (
            <div className="report-panel monthly-report card" style={{ padding: 0 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--iocl-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontFamily: 'Rajdhani', fontSize: 16, fontWeight: 700 }}>Employee Monthly Summary — {month}</div>
                  <div style={{ color: 'var(--iocl-muted)', marginTop: 4 }}>Click an employee name or "View Orders" to open a popup with their orders.</div>
                  <div className="print-report-note print-only">Billing period: {formatPrintDateLabel(month)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span className="text-orange" style={{ fontFamily: 'Rajdhani', fontSize: 20, fontWeight: 700 }}>Grand Total: {fmt(totalMonthly)}</span>
                  <button className="btn btn-success btn-sm" onClick={() => handlePrint('monthly-report')} disabled={!data.monthly?.data?.length}>Print Monthly Summary</button>
                </div>
              </div>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>#</th><th>Employee</th><th>Code</th><th>Department</th><th>Visits</th><th>Total Spent</th><th>Payment</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {data.monthly?.data?.length === 0 ? (
                      <tr><td colSpan={8}><div className="empty-state"><p>No data for this month</p></div></td></tr>
                    ) : data.monthly?.data?.map((row, i) => (
                      <tr key={row.employee_id}>
                        <td style={{ color: 'var(--iocl-muted)', fontSize: 12 }}>{i + 1}</td>
                        <td>
                          <button type="button" onClick={() => fetchEmployeeOrders(row)}
                            style={{ background: 'none', border: 'none', color: 'var(--iocl-orange)', cursor: 'pointer', padding: 0, font: 'inherit', textAlign: 'left', fontWeight: 500 }}>
                            {row.employee_name}
                          </button>
                        </td>
                        <td style={{ color: 'var(--iocl-muted)', fontFamily: 'monospace' }}>{row.emp_code || '—'}</td>
                        <td><span className="badge badge-blue">{row.department}</span></td>
                        <td>{row.visit_count > 0 ? <span className="badge badge-green">{row.visit_count}</span> : <span className="badge badge-red">0</span>}</td>
                        <td style={{ fontWeight: 600, color: row.total_spent > 0 ? 'var(--iocl-orange)' : 'var(--iocl-muted)' }}>{fmt(row.total_spent)}</td>
                        <td>
                          <select value={paymentStatus[row.employee_id] || 'Due'}
                            onChange={(e) => setPaymentStatus(prev => ({ ...prev, [row.employee_id]: e.target.value }))}
                            style={{ padding: '6px 8px', borderRadius: 6, border: '1px solid var(--iocl-border)', background: 'var(--iocl-panel)', color: paymentStatus[row.employee_id] === 'Paid' ? 'var(--iocl-green)' : 'var(--iocl-orange)', fontSize: 12 }}>
                            <option value="Paid">Paid</option>
                            <option value="Due">Due</option>
                          </select>
                        </td>
                        <td><button className="btn btn-secondary btn-sm" onClick={() => fetchEmployeeOrders(row)}>View Orders</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'items' && (
            <div className="report-panel card" style={{ padding: 0 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--iocl-border)' }}>
                <span style={{ fontFamily: 'Rajdhani', fontSize: 16, fontWeight: 700 }}>Item Consumption Report — {month}</span>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Item</th><th>Category</th><th>Unit Price</th><th>Total Qty</th><th>Revenue</th></tr></thead>
                  <tbody>
                    {data.items?.data?.length === 0 ? (
                      <tr><td colSpan={5}><div className="empty-state"><p>No data</p></div></td></tr>
                    ) : data.items?.data?.map(row => (
                      <tr key={row.item_name}>
                        <td style={{ color: 'var(--iocl-white)' }}>{row.item_name}</td>
                        <td><span className="badge badge-blue">{row.category}</span></td>
                        <td style={{ color: 'var(--iocl-muted)' }}>₹{row.price}</td>
                        <td>{row.total_qty > 0 ? <span className="badge badge-orange">{row.total_qty}</span> : '—'}</td>
                        <td style={{ fontWeight: 600, color: row.total_revenue > 0 ? 'var(--iocl-orange)' : 'var(--iocl-muted)' }}>{fmt(row.total_revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'daily' && (
            <div className="report-panel daily-report card" style={{ padding: 0 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--iocl-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontFamily: 'Rajdhani', fontSize: 16, fontWeight: 700 }}>Day-wise Totals — {month}</div>
                  <div style={{ color: 'var(--iocl-muted)', marginTop: 4 }}>Print the daily order totals for the selected month.</div>
                  <div className="print-report-note print-only">Billing period: {formatPrintDateLabel(month)}</div>
                </div>
                <button className="btn btn-success btn-sm" onClick={() => handlePrint('daily-report')} disabled={!data.daily?.data?.length}>Print Daily Totals</button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Date</th><th>Employees Served</th><th>Orders</th><th>Day Total</th></tr></thead>
                  <tbody>
                    {data.daily?.data?.length === 0 ? (
                      <tr><td colSpan={4}><div className="empty-state"><p>No records</p></div></td></tr>
                    ) : data.daily?.data?.map(row => (
                      <tr key={row.order_date}>
                        <td style={{ color: 'var(--iocl-white)', fontWeight: 500 }}>{row.order_date}</td>
                        <td><span className="badge badge-blue">{row.unique_employees}</span></td>
                        <td style={{ color: 'var(--iocl-light)' }}>{row.order_count}</td>
                        <td style={{ fontWeight: 700, color: 'var(--iocl-orange)' }}>{fmt(row.day_total)}</td>
                      </tr>
                    ))}
                    {data.daily?.data?.length > 0 && (
                      <tr className="total-row"><td>MONTH TOTAL</td><td></td><td></td><td>{fmt(data.daily?.data?.reduce((s, r) => s + r.day_total, 0))}</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Employee "View Orders" popup modal */}
          {selectedEmployee && (
            <div className="modal-overlay" onClick={() => setSelectedEmployee(null)}>
              <div className="modal modal-lg employee-report-section" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <div>
                    <div style={{ fontFamily: 'Rajdhani', fontSize: 16, fontWeight: 700 }}>{selectedEmployee.employee_name} — {month}</div>
                    <div style={{ color: 'var(--iocl-muted)', marginTop: 4, fontSize: 13 }}>
                      {selectedEmployee.department} · {selectedEmployee.emp_code || 'No code'} · {selectedEmployee.visit_count} visit(s) · {fmt(selectedEmployee.total_spent)}
                    </div>
                    <PeriodTabs value={employeeReportPeriod} onChange={setEmployeeReportPeriod}
                      disabled={ordersLoading || employeeOrders.length === 0} />
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button className="btn btn-success btn-sm"
                      onClick={() => handlePrint('employee-report', employeeReportPeriod === 'monthly' ? month : `${employeeReportPeriod} — ${month}`)}
                      disabled={ordersLoading || employeeOrders.length === 0}>
                      Print {titleCase(employeeReportPeriod)}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setSelectedEmployee(null)}>Close</button>
                  </div>
                </div>
                <div className="modal-content-scroll" style={{ padding: 16 }}>
                  {ordersLoading ? (
                    <div className="loading-center"><span className="spinner" /> Loading orders...</div>
                  ) : employeeOrders.length === 0 ? (
                    <div className="empty-state"><p>No orders found for this employee in {month}.</p></div>
                  ) : employeeReportPeriod === 'monthly' ? (
                    <table>
                      <thead><tr><th>Date</th><th>Order</th><th>Items</th><th>Total</th><th>Remarks</th></tr></thead>
                      <tbody>
                        {employeeOrders.map(order => (
                          <tr key={order.id}>
                            <td style={{ color: 'var(--iocl-white)', fontWeight: 500 }}>{order.order_date}</td>
                            <td style={{ color: 'var(--iocl-muted)', fontFamily: 'monospace' }}>#{order.id}</td>
                            <td style={{ whiteSpace: 'pre-line', color: 'var(--iocl-light)' }}>
                              {order.items.map(item => `${item.item_name} x${item.quantity} = ₹${item.subtotal.toFixed(2)}`).join('\n')}
                            </td>
                            <td style={{ fontWeight: 700, color: 'var(--iocl-orange)' }}>{fmt(order.total_amount)}</td>
                            <td>{order.remarks || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    (() => {
                      const groups = groupEmployeeOrdersDetailed(employeeOrders, employeeReportPeriod);
                      return (
                        <div>
                          {groups.map(group => (
                            <div key={group.key} style={{ marginBottom: 20 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--iocl-white)' }}>{group.label}</div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                  <span style={{ color: 'var(--iocl-muted)', fontSize: 12 }}>{group.order_count} orders · {fmt(group.total_amount)}</span>
                                  <button className="btn btn-secondary btn-sm" onClick={() => handlePrint('employee-day-report', group.key)} type="button">Print Day</button>
                                </div>
                              </div>
                              {group.orders.map(order => (
                                <div key={order.id} style={{ marginBottom: 12, border: '1px solid var(--iocl-border)', borderRadius: 8, overflow: 'hidden' }}>
                                  <div style={{ background: 'var(--iocl-panel)', padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                    <div style={{ fontWeight: 700, color: 'var(--iocl-white)' }}>Order #{order.id} — {order.order_date}</div>
                                    <div style={{ color: 'var(--iocl-muted)' }}>Total {fmt(order.total_amount)}</div>
                                  </div>
                                  <div style={{ padding: 10 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                      <thead>
                                        <tr>
                                          {['Item', 'Qty', 'Price', 'Subtotal'].map(h => (
                                            <th key={h} style={{ textAlign: h === 'Item' ? 'left' : 'right', padding: '5px 8px', borderBottom: '1px solid var(--iocl-border)' }}>{h}</th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {order.items.map(item => (
                                          <tr key={`${order.id}-${item.item_name}`}>
                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--iocl-border)', color: 'var(--iocl-white)' }}>{item.item_name}</td>
                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--iocl-border)', textAlign: 'right' }}>{item.quantity}</td>
                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--iocl-border)', textAlign: 'right' }}>{fmt(item.unit_price)}</td>
                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--iocl-border)', textAlign: 'right' }}>{fmt(item.subtotal)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    {order.remarks && <div style={{ marginTop: 8, color: 'var(--iocl-muted)', fontSize: 12 }}>Remarks: {order.remarks}</div>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      );
                    })()
                  )}
                </div>
                {selectedPrintGroup && (
                  <PrintInvoice
                    title={periodBillTitle(employeeReportPeriod === 'weekly' ? 'weekly' : 'daily')}
                    employee={selectedEmployeeForBill}
                    date={selectedPrintGroup.label} orders={selectedPrintGroup.orders} aggregateItems={false}
                  />
                )}
                {printMode === 'employee-report' && (
                  <PeriodInvoices period={employeeReportPeriod} employee={selectedEmployeeForBill}
                    periodLabel={month} orders={employeeOrders} />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
